/**
 * V5.3: 공휴일 관리 툴바
 *
 * 기능:
 * - 회사 선택 (KR/CN)
 * - 연도 선택
 * - 검색
 * - 유형/상태 필터
 * - 뷰 모드 전환 (목록/캘린더)
 * - 동기화, 추가 버튼
 *
 * V5.3 변경:
 * - Tailwind 클래스 제거, CSS 모듈로 전환
 */

import React from 'react';
import {
  Building2,
  Calendar,
  Search,
  RefreshCw,
  Download,
  Plus,
  List,
  CalendarDays,
  Filter,
  Copy,
} from 'lucide-react';
import {
  CompanyCode,
  HolidayType,
  ViewMode,
  HolidayFilters,
  HOLIDAY_TYPE_LABELS,
  YEAR_OPTIONS,
} from './types';
import styles from './holidaysStyles.module.css';

interface HolidayToolbarProps {
  // 회사/연도
  companyCode: CompanyCode;
  accessibleCompanies: CompanyCode[];
  selectedYear: number;
  onCompanyChange: (code: CompanyCode) => void;
  onYearChange: (year: number) => void;

  // 검색
  searchQuery: string;
  onSearchChange: (query: string) => void;

  // 필터
  filters: HolidayFilters;
  onFiltersChange: (filters: HolidayFilters) => void;

  // 뷰 모드
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;

  // 액션
  onRefresh: () => void;
  onSync?: () => void; // V60: optional for read-only
  onCopyFromLastYear?: () => void; // V5.12: 작년 복사
  onAdd?: () => void; // V60: optional for read-only
  isLoading: boolean;
  isMutating: boolean;
}

export function HolidayToolbar({
  companyCode,
  accessibleCompanies,
  selectedYear,
  onCompanyChange,
  onYearChange,
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  viewMode,
  onViewModeChange,
  onRefresh,
  onSync,
  onCopyFromLastYear,
  onAdd,
  isLoading,
  isMutating,
}: HolidayToolbarProps) {
  return (
    <div className={styles.toolbarContainer}>
      {/* 상단: 필터 영역 (grid 기반) */}
      <div className={styles.toolbarFilters}>
        {/* 회사 + 연도 (같은 셀에 묶음) */}
        <div className={styles.toolbarFilterGroup}>
          {accessibleCompanies.length > 1 && (
            <>
              <Building2 />
              <select
                value={companyCode}
                onChange={(e) => onCompanyChange(e.target.value as CompanyCode)}
                className={styles.toolbarSelect}
              >
                {accessibleCompanies.map((code) => (
                  <option key={code} value={code}>
                    {code === 'KR' ? '한국' : '중국'}
                  </option>
                ))}
              </select>
            </>
          )}
          <Calendar />
          <select
            value={selectedYear}
            onChange={(e) => onYearChange(Number(e.target.value))}
            className={styles.toolbarSelect}
          >
            {YEAR_OPTIONS.map((year) => (
              <option key={year} value={year}>
                {year}년
              </option>
            ))}
          </select>
        </div>

        {/* 유형 필터 */}
        <div className={styles.toolbarFilterGroup}>
          <Filter />
          <select
            value={filters.type}
            onChange={(e) =>
              onFiltersChange({ ...filters, type: e.target.value as HolidayType | 'all' })
            }
            className={styles.toolbarSelect}
            style={{ flex: 1 }}
          >
            <option value="all">전체 유형</option>
            {(Object.keys(HOLIDAY_TYPE_LABELS) as HolidayType[]).map((type) => (
              <option key={type} value={type}>
                {HOLIDAY_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>

        {/* 검색 + 새로고침 */}
        <div className={styles.toolbarSearchWrapper}>
          <div className={styles.searchInputWrapper}>
            <Search />
            <input
              type="text"
              placeholder="공휴일명, 날짜 검색..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className={styles.iconButton}
            title="새로고침"
          >
            <RefreshCw className={isLoading ? styles.spinning : ''} />
          </button>
        </div>
      </div>

      {/* 하단: 액션 버튼 영역 (항상 가로 정렬) */}
      <div className={styles.toolbarActions}>
        {/* 왼쪽: 뷰 모드 토글 */}
        <div className={styles.viewModeToggle}>
          <button
            onClick={() => onViewModeChange('list')}
            className={viewMode === 'list' ? styles.viewModeButtonActive : styles.viewModeButton}
            title="목록 보기"
          >
            <List />
          </button>
          <button
            onClick={() => onViewModeChange('calendar')}
            className={viewMode === 'calendar' ? styles.viewModeButtonActive : styles.viewModeButton}
            title="캘린더 보기"
          >
            <CalendarDays />
          </button>
        </div>

        {/* 오른쪽: 동기화/복사 + 추가 버튼 (V60: read-only 시 숨김) */}
        {(onSync || onCopyFromLastYear || onAdd) && (
          <div className={styles.actionButtons}>
            {companyCode === 'KR' && onSync ? (
              /* KR: 공공데이터포털 API 동기화 */
              <button
                onClick={onSync}
                disabled={isMutating}
                className={styles.syncButton}
                title="공공데이터포털 API에서 동기화"
              >
                <Download />
                <span className={styles.syncButtonText}>{selectedYear}년</span> 동기화
              </button>
            ) : companyCode !== 'KR' && onCopyFromLastYear ? (
              /* CN: 작년 공휴일 복사 */
              <button
                onClick={onCopyFromLastYear}
                disabled={isMutating}
                className={styles.copyButton}
                title={`${selectedYear - 1}년 공휴일을 ${selectedYear}년으로 복사`}
              >
                <Copy />
                <span className={styles.syncButtonText}>{selectedYear - 1}년</span> 복사
              </button>
            ) : null}
            {onAdd && (
              <button
                onClick={onAdd}
                disabled={isMutating}
                className={styles.addButton}
              >
                <Plus />
                <span className={styles.addButtonText}>공휴일</span> 추가
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
